import React, { useContext, useEffect, useState } from "react";
import {
  AvatarGroup,
  FreeCtaCard,
  PageLayout,
  Separator,
  Text,
} from "components";
import {
  ArrowRightIcon,
  InfiniteSpinnerIcon,
  LockIcon,
  MapPinLineIcon,
  StarIcon,
} from "components/icons";
import { CalendarCTA, CardBase, HomeHeader } from "../components";
import {
  AdHocBotInviteDisabledStyle,
  AdHocBotInviteStyle,
  DashobardContainer,
  GoToBillingButtonStyle,
  LocationContainerStyle,
  LocationIconStyle,
  ManageSubscriptionButtonStyle,
  MeetingInviteContainerStyle,
  MeetingInviteSentStyle,
  MeetingLinkIconStyle,
  MeetingListCardStyle,
  UpcomingCardRightStyle,
  UpcomingCardStyle,
  ViewMeetingContainerStyle,
} from "../styles";
import { UserContext } from "context";
import { EActivityEvent } from "types";
import { recordActivity } from "services";
import { useNavigate } from "react-router-dom";
import { E_Route } from "utils/constants";
import { MeetingListItem } from "features/meetings/components";
import { useMeeting } from "features/meetings/hooks";
import { css } from "@emotion/css";
import axios from "axios";
import { config } from "config";
import { usePageViewed, useSubscriptionPlan } from "hooks";
import { theme } from "assets/theme";

const date = Date.now();
export const DashboardLayout = () => {
  const { allMeetings, loading, externalMeetings } = useMeeting(date);
  const { state: userState } = useContext(UserContext);
  const [meetingUrl, setMeetingUrl] = useState<string>("");
  const [inviteSent, setInviteSent] = useState<boolean>(false);
  const [inviteState, setInviteState] = useState<string>("");

  usePageViewed(EActivityEvent.HomePageViewed);

  const { isFree, vipUser } = useSubscriptionPlan();
  const demoMode = false;

  const navigate = useNavigate();

  const handleMeetingClick = (id: string) => {
    const meeting = externalMeetings?.find(
      (m) => m.meetingId === id || m._id === id,
    );
    if (meeting) {
      if (userState?._id) {
        recordActivity({
          data: {
            user: userState?._id,
            event: EActivityEvent.MeetingPrepClicked,
            source: "web",
            data: {
              meetingId: id,
              startAt: meeting.startAt,
            },
          },
        });
      }
      navigate(`${E_Route.Meeting}/${id}`);
    } else {
      navigate(`${E_Route.Meeting}/${id}?internal=yes`);
    }
  };

  useEffect(() => {
    const loadInviteState = async () => {
      const res = await axios.get(
        `${config.apiUrl}/meeting-assistant/bot-status?id=${userState?._id}`,
      );
      if (res.data) {
        setInviteState(res.data);
      }
    };
    let interval: NodeJS.Timeout;
    if (inviteSent) {
      interval = setInterval(() => {
        loadInviteState();
      }, 5000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [userState?._id, inviteSent]);

  useEffect(() => {
    if (inviteState.toLowerCase().includes("ended")) {
      setTimeout(() => {
        setInviteSent(false);
        setInviteState("");
      }, 3000);
    }
  }, [inviteState]);

  const handleMeetingUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMeetingUrl(e.target.value);
  };

  const handleMeetingInvite = () => {
    if (meetingUrl.trim()) {
      axios
        .post(`${config.apiUrl}/meeting-assistant/invite-bot-to-meeting`, {
          meetingUrl,
          userId: userState?._id,
        })
        .then(() => {})
        .catch(() => {
          setInviteState("Error sending invite");
          setTimeout(() => {
            setInviteSent(false);
          }, 3000);
        });
      setInviteSent(true);
      //setTimeout(() => {
      //  setInviteSent(false);
      //}, 3000);
    }
  };

  const getNextMeeting = () => {
    const now = new Date();
    const nextMeeting = allMeetings.find((meeting) => {
      // const meetingDate = new Date(meeting.start || Date.now());
      const meetingEnd = new Date(meeting.end || Date.now());
      return meetingEnd > now && meeting.label === "External";
    });
    return nextMeeting;
  };

  const handleUpgrade = () => {
    navigate(E_Route.ManageBilling);
  };

  const nextMeeting = getNextMeeting();

  //const hasGmail = demoMode || user?.hasGmail;
  const hasCalendar = demoMode || userState?.hasCalendar;

  const locked = isFree && !vipUser;

  return (
    <PageLayout title="Home | Multiplayer">
      <div className={DashobardContainer}>
        {locked && <Text.H1>Hi, {userState?.firstName || "there"} 👋 </Text.H1>}

        {locked && (
          <div
            className={css`
              border-radius: 16px;
              background: #161617;
              width: 100%;
              padding: 40px 41px;
              margin-top: 24px;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              @media (max-width: 768px) {
                flex-direction: column;
                padding: 20px 0px;
                justify-content: center;
              }
            `}
          >
            <div
              className={css`
                width: 70%;
                @media (max-width: 768px) {
                  margin-bottom: 16px;
                }
              `}
            >
              <Text.BodyXS color="highlight/1">FREE PLAN</Text.BodyXS>
              <Separator.Horizontal spacing={2} />
              <Text.BodySM>
                You are currently on a Free plan. This plan includes labeling
                and archiving 300 emails per month, and does not include
                Executive Assistant features.
              </Text.BodySM>
              <Separator.Horizontal spacing={3} />
              <Text.BodySM>
                You can check your remaining credits, or switch to paid plan and
                unlock all features in the Settings page.
              </Text.BodySM>
            </div>
            <div>
              <button
                className={GoToBillingButtonStyle}
                onClick={handleUpgrade}
              >
                Go to Billing
              </button>
            </div>
          </div>
        )}

        {!locked && <HomeHeader />}
        <Separator.Horizontal spacing={4} />

        {!hasCalendar && !locked && <CalendarCTA />}

        <Separator.Horizontal spacing={4} />

        {nextMeeting && !locked && (
          <>
            <Text.Body2XS>UPCOMING</Text.Body2XS>
            <CardBase
              className={UpcomingCardStyle}
              onClick={() => handleMeetingClick(nextMeeting?.id!)}
            >
              <div>
                <Text.Body2XS>
                  {nextMeeting?.startAt?.split(" ")?.[0] || "/"} -{" "}
                  {nextMeeting?.endAt || "/"}
                </Text.Body2XS>
                <Text.Body>{nextMeeting?.title || "Calendar event"}</Text.Body>
                <div className={LocationContainerStyle}>
                  <MapPinLineIcon fill="darkGrey" style={LocationIconStyle} />
                  <Text.Body2XS color="darkGrey">
                    {nextMeeting?.location || "-"}
                  </Text.Body2XS>
                </div>
              </div>
              <div className={UpcomingCardRightStyle}>
                <AvatarGroup
                  users={
                    nextMeeting?.attendees?.map((a) => ({
                      src: a.source,
                      username: a.userName,
                    })) || []
                  }
                  max={3}
                />
                <div className={ViewMeetingContainerStyle}>
                  <Text.BodyXS
                    color="highlight/1"
                    className={css`
                      @media (max-width: 768px) {
                        display: none;
                      }
                    `}
                  >
                    View prep
                  </Text.BodyXS>
                  <ArrowRightIcon
                    fill="highlight/1"
                    style={MeetingLinkIconStyle}
                  />
                </div>
              </div>
            </CardBase>
          </>
        )}

        {!locked && (
          <div className={AdHocBotInviteStyle}>
            {isFree && !vipUser && (
              <div className={AdHocBotInviteDisabledStyle}>
                <div
                  className={css`
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                  `}
                >
                  <LockIcon
                    fill="primary"
                    style={{
                      width: "32px",
                      height: "32px",
                      marginRight: "8px",
                    }}
                  />
                  <Text.BodyXS color="primary">
                    Enable Multiplayer AI note taker by upgrading your account
                  </Text.BodyXS>
                </div>
                <button
                  className={ManageSubscriptionButtonStyle}
                  onClick={handleUpgrade}
                >
                  Upgrade plan
                </button>
              </div>
            )}
            <CardBase className={MeetingListCardStyle}>
              <div
                className={css`
                  width: 100%;
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                `}
              >
                <div
                  className={css`
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background-color: #8d86f8;
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    margin-right: 8px;
                  `}
                >
                  <StarIcon
                    fill="dark"
                    style={{
                      width: "10px",
                      height: "10px",
                    }}
                  />
                </div>
                <div
                  style={{
                    width: "90%",
                  }}
                >
                  <Text.BodyXS color="primary">
                    Invite Multiplayer AI note taker to your meeting
                  </Text.BodyXS>
                  <Text.Body2XS color="seconday">
                    Just provide the meeting link and Multiplayer will join and
                    take notes for you!
                  </Text.Body2XS>
                </div>
              </div>
              <Separator.Horizontal spacing={1} />
              {inviteSent ? (
                <div className={MeetingInviteSentStyle}>
                  <Text.BodyXS color="primary">
                    Invitation received:{" "}
                  </Text.BodyXS>
                  <Text.BodyXS
                    color="highlight/1"
                    style={{ marginLeft: "8px" }}
                  >
                    {inviteState || "Joining the call "}
                  </Text.BodyXS>

                  <InfiniteSpinnerIcon
                    fill="highlight/1"
                    style={{ height: "16px" }}
                  />
                </div>
              ) : (
                <div className={MeetingInviteContainerStyle}>
                  <input
                    placeholder="Meeting url"
                    style={{
                      width: "83%",
                      backgroundColor: "transparent",
                      outline: "none",
                      height: "40px",
                      borderWidth: "0px",
                    }}
                    className={css`
                      width: 83%;
                      background-color: transparent;
                      outline: none;
                      box-sizing: border-box;
                      height: 40px;
                      border-width: 0px;
                      padding: 0 16px;
                      font-size: ${theme.font["body-xs"].fontSize};
                      color: ${theme.colors.text.primary};
                      &::placeholder {
                        color: ${theme.colors.text.seconday};
                      }
                    `}
                    onChange={handleMeetingUrlChange}
                  />
                  <button
                    className={ManageSubscriptionButtonStyle}
                    onClick={handleMeetingInvite}
                  >
                    Invite
                  </button>
                </div>
              )}
              <Separator.Horizontal spacing={1} />
              <Text.Body2XS color="seconday" style={{ color: "#8C83C3" }}>
                Or invite Multiplayer directly from your meeting
                multiplayer@multiplayer.work
              </Text.Body2XS>
            </CardBase>
          </div>
        )}

        {locked && <FreeCtaCard />}

        {!locked && <Text.Body2XS>CALENDAR</Text.Body2XS>}
        {!locked && (
          <CardBase className={MeetingListCardStyle}>
            {loading && <Text.Body>Loading...</Text.Body>}
            {allMeetings.map((meeting, idx, all) => (
              <React.Fragment key={meeting.id}>
                <MeetingListItem
                  title={meeting.title}
                  startTime={meeting.startAt || "Unknown"}
                  type={meeting.label || "External"}
                  onClick={() => {
                    handleMeetingClick(meeting.id);
                  }}
                />
                {idx !== all.length - 1 && (
                  <Separator.Horizontal border spacing={1} />
                )}
              </React.Fragment>
            ))}
            {!allMeetings.length && !loading && (
              <Text.Body>Nothing on your agenda!</Text.Body>
            )}
          </CardBase>
        )}
      </div>
    </PageLayout>
  );
};
